import React from 'react';
import ClickOutsideTrap from './ClickOutsideTrap';
import FocusTrapComponent from './react-focus-trap/focus-trap';

interface Props {
  /** Enable or disable all traps */
  enabled?: boolean;
  onExit?: (event: React.KeyboardEvent | React.MouseEvent | MouseEvent) => unknown;
  children: React.ReactNode;
  /** Stop ESC keypress from propagating -- useful for nested Popovers */
  stopEscPropagation: boolean;
  shouldExitOnClickOutside?: boolean;
  shouldExitOnEsc?: boolean;
  className?: string;
  style?: React.CSSProperties;
}

class Trap extends React.PureComponent<Props> {
  static defaultProps = {
    enabled: true,
    stopEscPropagation: true,
    shouldExitOnClickOutside: true,
    shouldExitOnEsc: true,
  };

  clickOutsideRef = React.createRef<HTMLDivElement>();

  handleClickOutside = (event: React.MouseEvent | MouseEvent) => {
    if (!this.props.shouldExitOnClickOutside) {
      return;
    }

    if (!this.clickOutsideRef.current) {
      this.props.onExit?.(event);
      return;
    }

    const bounds = this.clickOutsideRef.current.getBoundingClientRect();
    if (
      event.clientX < bounds.left ||
      event.clientX > bounds.right ||
      event.clientY < bounds.top ||
      event.clientY > bounds.bottom
    ) {
      this.props.onExit?.(event);
    }
  };

  handleExit = (event: React.KeyboardEvent) => {
    if (!this.props.shouldExitOnEsc || event.key !== 'Escape') {
      return;
    }
    if (this.props.stopEscPropagation) {
      event.stopPropagation();
    }
    this.props.onExit?.(event);
  };

  render() {
    const {
      children,
      onExit,
      enabled,
      shouldExitOnEsc,
      shouldExitOnClickOutside,
      stopEscPropagation,
      ...rest
    } = this.props;

    if (!enabled) {
      return <div {...rest}>{children}</div>;
    }

    return (
      <ClickOutsideTrap
        onClickOutside={this.handleClickOutside}
        forwardedRef={this.clickOutsideRef}
        {...rest}
      >
        {/* When !active, children returns directly without any wrappers */}
        <FocusTrapComponent active={this.props.shouldExitOnEsc} onExit={this.handleExit}>
          {children}
        </FocusTrapComponent>
      </ClickOutsideTrap>
    );
  }
}

export default Trap;
